import React from "react";
import {
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Pagination,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchBookings, retrySyncBooking } from "../api/bookings";
import type { Booking } from "../api/bookings";
import { useBookingsStore } from "../store/bookingsSlice";
import type { BookingModal, PendingSyncRetry } from "../store/bookingsSlice";
import { BookingsTable } from "../components/bookings/BookingsTable";
import { CancelBookingDialog } from "../components/bookings/CancelBookingDialog";
import { RescheduleBookingModal } from "../components/bookings/RescheduleBookingModal";
import { EditBookingModal } from "../components/bookings/EditBookingModal";
import { BookingDetailPanel } from "../components/bookings/BookingDetailPanel";

const Bookings: React.FC = () => {
  const {
    filters,
    page,
    pageSize,
    setFilters,
    setPage,
    openModal,
    closeModal,
    activeModal,
    selectedBookingId,
    pendingSyncRetry,
    setPendingSyncRetry,
  } = useBookingsStore();

  const { data, isLoading } = useQuery({
    queryKey: ["bookings", filters, page, pageSize],
    queryFn: () =>
      fetchBookings({
        page,
        page_size: pageSize,
        date_from: filters.dateFrom ?? undefined,
        date_to: filters.dateTo ?? undefined,
        service: filters.service || undefined,
        status: filters.status || undefined,
        payment_status: filters.paymentStatus || undefined,
      }),
  });

  const retryMutation = useMutation({
    mutationFn: (payload: PendingSyncRetry) =>
      retrySyncBooking(payload.bookingId, payload.action, {
        newDatetime: payload.newDatetime,
        newTitle: payload.newTitle,
      }),
    onSettled: () => setPendingSyncRetry(null),
  });

  const bookings: Booking[] = data?.bookings ?? [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;
  const selectedBooking =
    bookings.find((b) => b.booking_id === selectedBookingId) ?? null;

  const handleAction = (action: BookingModal, booking: Booking) => {
    if (action) openModal(action, booking.booking_id);
  };

  const handleRowClick = (booking: Booking) => {
    openModal("detail", booking.booking_id);
  };

  const handleSyncFailed = (payload: PendingSyncRetry) => {
    setPendingSyncRetry(payload);
  };

  return (
    <Paper sx={{ padding: 2, marginTop: 2, borderRadius: 2, boxShadow: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Bookings
      </Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            label="From Date"
            type="date"
            fullWidth
            size="small"
            value={filters.dateFrom ?? ""}
            onChange={(e) =>
              setFilters({ dateFrom: e.target.value || null })
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="To Date"
            type="date"
            fullWidth
            size="small"
            value={filters.dateTo ?? ""}
            onChange={(e) =>
              setFilters({ dateTo: e.target.value || null })
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Service"
            fullWidth
            size="small"
            value={filters.service}
            onChange={(e) => setFilters({ service: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Payment</InputLabel>
            <Select
              label="Payment"
              value={filters.paymentStatus}
              onChange={(e) =>
                setFilters({ paymentStatus: e.target.value as typeof filters.paymentStatus })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="checkout_created">Checkout created</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="partially_refunded">Partially refunded</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="disputed">Disputed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <BookingsTable
        bookings={bookings}
        loading={isLoading}
        onAction={handleAction}
        onRowClick={handleRowClick}
        paymentStatusFilter={filters.paymentStatus}
        onPaymentStatusChange={(v) => setFilters({ paymentStatus: v })}
      />

      {totalPages > 1 && (
        <Grid container justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Grid>
      )}

      <CancelBookingDialog
        booking={activeModal === "cancel" ? selectedBooking : null}
        onClose={closeModal}
        onSyncFailed={handleSyncFailed}
      />
      <RescheduleBookingModal
        booking={activeModal === "reschedule" ? selectedBooking : null}
        onClose={closeModal}
        onSyncFailed={handleSyncFailed}
      />
      <EditBookingModal
        booking={activeModal === "edit" ? selectedBooking : null}
        onClose={closeModal}
        onSyncFailed={handleSyncFailed}
      />
      <BookingDetailPanel
        booking={activeModal === "detail" ? selectedBooking : null}
        onClose={closeModal}
      />

      <Snackbar
        open={pendingSyncRetry !== null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          action={
            <>
              <Button
                size="small"
                color="inherit"
                disabled={retryMutation.isPending}
                onClick={() => {
                  if (pendingSyncRetry) retryMutation.mutate(pendingSyncRetry);
                }}
              >
                Retry Sync
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => setPendingSyncRetry(null)}
              >
                Dismiss
              </Button>
            </>
          }
        >
          Calendar sync failed. The booking was saved but the calendar event
          could not be updated.
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default Bookings;
